import { Badge } from "@/components/ui";
import { cn, toYMD } from "@/lib/utils";
import {
  useGetCrmClassQuery,
  useDeleteCrmClassMutation,
} from "@/services/school";
import { Trash2 } from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

const useCurriculumInfo = () => {
  const { action, id } = useParams();
  const [pageNumber, setPageNumber] = useState<number>(0); // same as given code
  const [pageSize, setPageSize] = useState<number>(10);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingCurriculum, setEditingCurriculum] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingRow, setDeletingRow] = useState<any>(null);

  const queryParams = {
    page: pageNumber + 1,
    limit: pageSize,
    school: id,
  };

  const {
    data: curriculumData,
    isLoading,
    isError,
    error,
  } = useGetCrmClassQuery(queryParams);

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  const deleteCrmClass = useDeleteCrmClassMutation({
    onSuccess: () => {
      setIsDeleteDialogOpen(false);
      setDeletingRow(null);
    },
    onError: (error) => {
      console.error("Error deleting CRM class:", error);
    },
  });

  const onRowClick = (rowData: any) => {
    console.log("Row clicked:", rowData);
  };

  const columns = [
    {
      accessorKey: "curriculum_grade",
      header: <div>Grade Name</div>,
      size: 120,
    },
    {
      accessorKey: "year",
      header: <div>Year</div>,
      accessorFn: (row: any) => String(row?.year || ""),
      size: 80,
    },
    {
      accessorKey: "section",
      header: <div>Sections</div>,
      cell: (row: any) => {
        const sections = row?.row?.original?.section || [];
        const sectionNames = sections
          .map((section: any) => section.name)
          .join(", ");
        return <div className="text-sm">{sectionNames || "-"}</div>;
      },
      size: 200,
    },
    {
      accessorKey: "totalStudents",
      header: <div>Total Students</div>,
      cell: (row: any) => {
        return (
          <span className="font-medium">
            {row?.row?.original?.total_students || 0}
          </span>
        );
      },
      size: 120,
    },
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) => String(toYMD(row?.createdAt) || ""),
      size: 100,
    },
   {
      accessorKey: "action",
      header: <div>Action</div>,
      cell: (row: any) =>
        !hasRole("SALES_OPS") && !hasRole("INVENTORY_TEAM") && !hasRole("LABS_TEAM") && !hasRole("FINANCE_TEAM") ? (
          <Trash2
            className="h-4 stroke-red-600 cursor-pointer hover:stroke-red-700"
            onClick={() => handleDeleteClick(row.row.original)}
          />
        ) : (
          <Trash2 className="h-4 stroke-gray-400 cursor-not-allowed opacity-50" />
        ),
      size: 80,
    }
  ];

  const handleAddNew = () => {
    setEditingCurriculum(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCurriculum(null);
  };

  const handleDeleteClick = (row: any) => {
    setDeletingRow(row);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = (reason: string) => {
    if (deletingRow && id) {
      deleteCrmClass.mutate({
        classId: deletingRow.class_id,
        schoolId: id,
      });
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDeletingRow(null);
  };

  const tableData = curriculumData?.data?.[0]?.records || [];
  const totalRecords =
    curriculumData?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  return {
    columns,
    tableData,
    pageNumber,
    pageSize,
    totalRecords,
    totalPages,
    setPageNumber,
    setPageSize,
    handleAddNew,
    isFormOpen,
    editingCurriculum,
    handleFormClose,
    isLoading,
    isError,
    error,
    // Delete functionality
    isDeleteDialogOpen,
    deletingRow,
    handleDeleteConfirm,
    handleDeleteCancel,
    isDeleting: deleteCrmClass.isPending,
  };
};

export default useCurriculumInfo;
